import React from 'lib/react';
import Typography from 'commonComponents/typography';
import { useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import {
  EVGrid,
  EVPaper,
  EVTypography,
  EVIconButton,
  EVEditOutlined, EVKeyboardArrowRight,
  EVSkeleton,
} from 'lib/components';

import { getCurrencySymbol } from 'features/order/Order.utils';
import { Link } from 'lib/router';
import { CONSUMPTION_ROUTE, CONTACT_INFORMATION_ROUTE } from 'constants/RouteConstants';
import DashboardRetry from 'features/dashboard/DashboardRetry.component';
import SubscriptionInformationStyles from './SubscriptionInformationStyles';
import SubscriptionCard from './SubscriptionCard.component';
import SubscriptionInformationLoader from './SubscriptionInformationLoader';

function SubscriptionInformation() {
  const classes = SubscriptionInformationStyles();
  const { t } = useTranslation();

  // #genai
  const {
    subscriptionDashboardData,
    subscriptionDashboardLoading,
    subscriptionDashboardFailure,
  } = useSelector(({ userData }) => ({
    subscriptionDashboardData: userData.subscriptionDashboard,
    subscriptionDashboardLoading: userData.loading.subscriptionDashboard,
    subscriptionDashboardFailure: userData.error.subscriptionDashboard,
  }));

  const subscriptions = subscriptionDashboardData?.subscriptions || [];
  const fees = subscriptionDashboardData?.fees || {
    nonSubscription: 0,
    deliveryFees: 0,
    integrationFees: 0,
  };

  const {
    companyInformation,
    companyInformationLoading,
    companyInformationFailure,
  } = useSelector(({ userData }) => ({
    companyInformation: userData.companyInformation,
    companyInformationLoading: userData.loading.companyInformation,
    companyInformationFailure: userData.error.companyInformation,
  }));

  const {
    company, address1, city, state, zip,
  } = companyInformation.address || {};

  // Calculate current period (October 2025)
  const currentDate = new Date();
  const currentPeriod = `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`;

  // Calculate overages
  const calculateOverage = (subscription) => {
    if (subscription.usedQty > subscription.includedQty) {
      return subscription.usedQty - subscription.includedQty;
    }
    return 0;
  };

  // Calculate XL fees (sum of all overages * overage rate)
  const overageFees = subscriptions.reduce((total, sub) => {
    const overage = calculateOverage(sub);
    return total + (overage * sub.overageRatePerUnit);
  }, 0);

  const totalMonthlySurchargeFees = overageFees + fees.nonSubscription + fees.deliveryFees + fees.integrationFees;
  // #genai - Resolve company status states without a nested conditional expression.
  let companyInformationContent = (
    <EVGrid className={classes.companyInfoContainer}>
      <EVTypography className={classes.companyName}>{company}</EVTypography>
      <EVTypography className={classes.companyAddress}>
        {address1}
        <br />
        {`${city}, ${state} ${zip}`}
      </EVTypography>
      <EVIconButton className={classes.editButton} component={Link} to={CONTACT_INFORMATION_ROUTE}>
        <EVEditOutlined htmlColor="#009AC5" />
      </EVIconButton>
    </EVGrid>
  );
  if (companyInformationLoading) {
    companyInformationContent = <EVSkeleton style={{ height: '24px', width: '40%', borderRadius: '6px' }} animation="wave" variant="rect" />;
  } else if (companyInformationFailure) {
    companyInformationContent = (
      <EVGrid>
        <DashboardRetry
          retryText={t('dashboard__company_info_retry_msg')}
          action="LOAD_COMPANY_INFO"
          style={{ width: 'max-content' }}
        />
      </EVGrid>
    );
  }

  // #genai - Resolve dashboard status states before rendering the container.
  let subscriptionDashboardContent = (
    <EVGrid className={classes.subscriptionsContainer}>
      <EVGrid className={classes.currentPeriodContainer}>
        <Typography className={classes.currentPeriodText}>
          {currentPeriod}
        </Typography>
        <Link to={CONSUMPTION_ROUTE} className={classes.viewConsumptionLink}>
          {t('subscription_info__view_consumption_details')}
          <EVKeyboardArrowRight />
        </Link>
      </EVGrid>
      <EVGrid className={classes.subscriptionsAndSummaryContainer}>
        <EVGrid className={classes.subscriptionCardsContainer}>
          {subscriptions.map((subscription) => (
            <SubscriptionCard key={subscription.id} subscription={subscription} />
          ))}
        </EVGrid>
        <EVGrid className={classes.summaryPaper}>
          <EVGrid className={classes.summaryContent}>
            <EVGrid className={classes.summaryRow}>
              <Typography variant="subtitle-600" fontColor="charcoal-600">
                {t('subscription_info__overages')}
              </Typography>
              <Typography variant="h3-400" fontColor="charcoal-800">
                {getCurrencySymbol(1)}
                {overageFees.toFixed(2)}
              </Typography>
            </EVGrid>
            <EVGrid className={classes.summaryRow}>
              <Typography variant="subtitle-600" fontColor="charcoal-600">
                {t('subscription_info__non_subscription')}
              </Typography>
              <Typography variant="h3-400" fontColor="charcoal-800">
                {getCurrencySymbol(1)}
                {fees.nonSubscription.toFixed(2)}
              </Typography>
            </EVGrid>
            <EVGrid className={classes.summaryRow}>
              <Typography variant="subtitle-600" fontColor="charcoal-600">
                {t('subscription_info__delivery_fees')}
              </Typography>
              <Typography variant="h3-400" fontColor="charcoal-800">
                {getCurrencySymbol(1)}
                {fees.deliveryFees.toFixed(2)}
              </Typography>
            </EVGrid>
            <EVGrid className={classes.summaryRow}>
              <Typography variant="subtitle-600" fontColor="charcoal-600">
                {t('subscription_info__integration_fees')}
              </Typography>
              <Typography variant="h3-400" fontColor="charcoal-800">
                {getCurrencySymbol(1)}
                {fees.integrationFees.toFixed(2)}
              </Typography>
            </EVGrid>
            <EVGrid className={classes.summaryDivider} />
            <EVGrid className={classes.summaryRow}>
              <Typography variant="subtitle-600" fontColor="charcoal-800" className={classes.totalLabel}>
                {t('subscription_info__total_monthly_surcharge_fees')}
              </Typography>
              <Typography variant="h3-700" fontColor="charcoal-800">
                {getCurrencySymbol(1)}
                {totalMonthlySurchargeFees.toFixed(2)}
              </Typography>
            </EVGrid>
          </EVGrid>
        </EVGrid>
      </EVGrid>
    </EVGrid>
  );
  if (subscriptionDashboardLoading) {
    subscriptionDashboardContent = <SubscriptionInformationLoader />;
  } else if (subscriptionDashboardFailure) {
    subscriptionDashboardContent = (
      <DashboardRetry
        retryText={t('subscription_dashboard__subscription_dashboard_retry_msg')}
        action="LOAD_SUBSCRIPTION_DASHBOARD"
      />
    );
  }

  return (
    <EVPaper className={classes.container}>
      <EVGrid className={classes.titleContainer}>
        <EVTypography variant="h2-400" fontColor="charcoal-900" className={classes.title}>
          {t('subscription_info__title')}
        </EVTypography>

        {/* Company Information Section */}
        {companyInformationContent}
      </EVGrid>

      {/* Subscriptions and Summary Container */}
      {subscriptionDashboardContent}
    </EVPaper>
  );
}

export default SubscriptionInformation;
